import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Activity, CalendarCheck, Euro, Gauge, UserPlus, Users } from 'lucide-react';
import { Button } from '~/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, Separator } from '~/components/ui/primitives';
import {
  ErrorState,
  KpiCard,
  LoadingState,
  PageHeader,
  StatusBadge,
} from '~/components/shared/states';
import { api } from '~/lib/api';
import { queryKeys } from '~/lib/queries';
import { formatPrice } from '~/lib/utils';
import { type Dashboard } from '@hch/shared';

export function AdminDashboardPage() {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: queryKeys.dashboard,
    queryFn: () => api.get<Dashboard>('/admin/dashboard'),
  });

  if (isLoading)
    return (
      <div className="app-page">
        <LoadingState rows={3} />
      </div>
    );
  if (error)
    return (
      <div className="app-page">
        <ErrorState error={error} onRetry={() => void refetch()} />
      </div>
    );
  if (!data) return null;

  return (
    <div className="app-page">
      <PageHeader title="Tableau de bord" description="Vue d'ensemble de l'activite." />

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard
          label="Interventions aujourd'hui"
          value={data.interventions.today}
          icon={CalendarCheck}
        />
        <KpiCard label="A venir" value={data.interventions.upcoming} icon={Activity} />
        <KpiCard
          label="En attente d'encaissement"
          value={data.interventions.pendingPayment}
          icon={Euro}
          tone={data.interventions.pendingPayment > 0 ? 'warning' : 'default'}
        />
        <KpiCard
          label="Chiffre d'affaires (30 j)"
          value={formatPrice(data.revenue30Days.total)}
          hint={`${data.revenue30Days.count} encaissement${data.revenue30Days.count > 1 ? 's' : ''}`}
          icon={Euro}
          tone="success"
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Capacite</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div className="app-summary-row">
              <span className="text-muted-foreground">Techniciens actifs</span>
              <span className="font-medium tabular-nums">{data.capacity.technicians}</span>
            </div>
            <div className="app-summary-row">
              <span className="text-muted-foreground">Zones desservies</span>
              <span className="font-medium tabular-nums">{data.capacity.zones}</span>
            </div>
            <div className="app-summary-row">
              <span className="text-muted-foreground">Creneaux a venir</span>
              <span className="font-medium tabular-nums">{data.capacity.futureSlots}</span>
            </div>

            <Separator />

            <div className="space-y-1.5">
              <div className="app-summary-row">
                <span className="flex items-center gap-1.5 text-muted-foreground">
                  <Gauge className="size-4" aria-hidden />
                  Taux d'occupation
                </span>
                <span className="font-semibold tabular-nums">{data.capacity.occupancyRate} %</span>
              </div>
              {/* Barre decorative : la valeur chiffree ci-dessus porte l'information. */}
              <div className="h-2 overflow-hidden rounded-full bg-muted" aria-hidden>
                <div
                  className="h-full rounded-full bg-primary transition-all"
                  style={{ width: `${Math.min(100, data.capacity.occupancyRate)}%` }}
                />
              </div>
              <p className="text-xs text-muted-foreground">
                {data.capacity.availableSlots} creneaux encore disponibles.
                {data.capacity.occupancyRate > 80
                  ? ' Pensez a generer de nouveaux creneaux.'
                  : null}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Repartition des interventions (30 jours)</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {Object.entries(data.interventions.byStatus).map(([status, count]) => (
              <div key={status} className="app-summary-row">
                <StatusBadge status={status as never} />
                <span className="font-medium tabular-nums">{count}</span>
              </div>
            ))}

            <Separator />

            <div className="app-summary-row">
              <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
                <Users className="size-4" aria-hidden />
                Clients actifs
              </span>
              <span className="font-medium tabular-nums">{data.clients.active}</span>
            </div>

            {data.clients.waitingList > 0 ? (
              <Button asChild variant="outline" size="sm" className="w-full">
                <Link to="/admin/liste-attente">
                  <UserPlus aria-hidden />
                  {data.clients.waitingList} prospect(s) hors zone a traiter
                </Link>
              </Button>
            ) : null}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// Interventions
